
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Shield, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { passwordRequirements } from "@/utils/validation";

interface UpdatePasswordFormProps {
  onSubmit: (password: string) => Promise<{
    success: boolean;
    message?: string;
  }>;
  loading: boolean;
}

const UpdatePasswordForm = ({ onSubmit, loading }: UpdatePasswordFormProps) => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState<{[key: string]: boolean}>({});
  const [passwordMatch, setPasswordMatch] = useState(false);

  useEffect(() => {
    const strength = passwordRequirements.reduce((acc, req) => ({
      ...acc,
      [req.label]: req.regex.test(password)
    }), {});
    setPasswordStrength(strength);
    setPasswordMatch(password === confirmPassword && password !== "");
  }, [password, confirmPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!passwordRequirements.every(req => req.regex.test(password))) {
      setError("Le mot de passe ne respecte pas les critères de sécurité");
      return;
    }

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    try {
      const result = await onSubmit(password);
      if (result.success) {
        navigate("/account", { replace: true });
      } else {
        setError(result.message || "Une erreur est survenue");
      }
    } catch (error) {
      setError("Une erreur est survenue lors de la mise à jour");
    }
  };

  return (
    <div className="w-full max-w-md mx-auto p-6 space-y-6 bg-card text-card-foreground shadow-sm rounded-lg border">
      <div className="space-y-2 text-center">
        <h2 className="text-2xl font-semibold tracking-tight">
          Définir un nouveau mot de passe
        </h2>
        <p className="text-sm text-gray-600">
          Choisissez un mot de passe sécurisé
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 rounded bg-destructive/15 text-destructive text-sm">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="password" className="block text-sm font-medium mb-1">
            Nouveau mot de passe
          </label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="pl-10"
              placeholder="••••••••"
              maxLength={100}
            />
            <Shield className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-gray-500" />
              ) : (
                <Eye className="h-4 w-4 text-gray-500" />
              )}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium mb-1">
            Confirmer le mot de passe
          </label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className={`pl-10 ${
                confirmPassword && (passwordMatch ? "border-green-500" : "border-red-500")
              }`}
              placeholder="••••••••"
              maxLength={100}
            />
            <Shield className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              {showConfirmPassword ? (
                <EyeOff className="h-4 w-4 text-gray-500" />
              ) : (
                <Eye className="h-4 w-4 text-gray-500" />
              )}
            </button>
          </div>
          {confirmPassword && (
            <div className="mt-1 flex items-center gap-2">
              {passwordMatch ? (
                <>
                  <Check className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600">
                    Les mots de passe correspondent
                  </span>
                </>
              ) : (
                <>
                  <X className="h-4 w-4 text-red-500" />
                  <span className="text-sm text-red-600">
                    Les mots de passe ne correspondent pas
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-purple-500" />
            <span className="text-sm font-medium text-gray-700">
              Critères de sécurité
            </span>
          </div>
          <div className="space-y-2">
            {passwordRequirements.map((req) => (
              <div key={req.label} className="flex items-center gap-2">
                {passwordStrength[req.label] ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <X className="h-4 w-4 text-gray-400" />
                )}
                <span className={`text-sm ${
                  passwordStrength[req.label] ? "text-green-600" : "text-gray-600"
                }`}>
                  {req.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? (
            <span className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Mise à jour...
            </span>
          ) : "Mettre à jour le mot de passe"}
        </Button>
      </form>
    </div>
  );
};

export default UpdatePasswordForm;
